import { isValidElement, useContext, useEffect, useState } from 'react';
import { REMOVE_TOAST } from '../Types/toastTypes';
import { ToasterStoreContext } from './ToasterStoreContext';

/**
 * Sends an add toast message to the top-level parent window.
 *
 * @param {string} title - The title of the message.
 * @param {string} message - The content of the message.
 * @param {string | undefined} host - The host of the parent window.
 * @param {string | undefined} serializedImage - The serialized image data (optional).
 * @param {string} toastId - The ID of the toast.
 * @param {string} containerId - The ID of the container to display the toast.
 * @returns {null} - Returns null if an error occurred.
 */
import { jsx as _jsx } from "react/jsx-runtime";
const sendToTopLevelParent = (title, message, host, serializedImage, toastId, containerId) => {
  let currentWindow = window.parent;
  const options = {
    serializedImage,
    originToastId: toastId,
    originContainerId: containerId
  };
  try {
    while (currentWindow.parent !== currentWindow) {
      currentWindow.postMessage({
        type: 'ADD_TOAST',
        title,
        message,
        options
      }, host);
      currentWindow = currentWindow.parent;
    }
  } catch (error) {
    return null;
  }
};

// resolve what to render, if it is a valid react element then render it, otherwise if it is a string then display an img tag
/**
 * Resolves the given image into a proper JSX element to be used in a toast.
 *
 * The image can either be a React element, a string representing the URL of an image, or falsy.
 * If the image is a React element, it will be returned as is.
 * If the image is a string representing the URL of an image, an <img> element with the proper styling
 * will be returned with the image's URL set as the source.
 * If the image is falsy, null will be returned.
 *
 * @param {ReactElement|string} image - The image to resolve.
 * @returns {ReactElement|null} - The resolved JSX element or null if the image is invalid or falsy.
 */
const resolveImage = image => {
  if ( /*#__PURE__*/isValidElement(image)) {
    return image;
  } else if (typeof image === 'string') {
    return /*#__PURE__*/_jsx("img", {
      style: {
        maxHeight: '40px',
        maxWidth: '40px',
        height: '40px',
        width: '40px'
      },
      src: image,
      alt: "Toast"
    });
  } else {
    return null;
  }
};

/**
 * Represents a ToastProxy component.
 * The ToastProxy represents a toast from a child window, such as an iframe that should display at the top of the DOM
 *
 *
 * @param {ToastContainerProps} props - The props for the ToastProxy component.
 * @returns {JSX.Element} - The rendered ToastProxy component.
 */
export const ToastProxy = props => {
  const {
    title,
    message,
    host,
    image,
    toastId,
    containerId,
    onClick
  } = props;
  const [base64Image, setBase64Image] = useState(null);
  const context = useContext(ToasterStoreContext);

  // handle the parent message to remove toast and execute onClick if it was an onclick event
  const handleParentMessage = event => {
    if (event.data && event.data.type === REMOVE_TOAST) {
      context?.dispatch({
        type: REMOVE_TOAST,
        entry: {
          ...props
        }
      });
      if (event.data.action === 'on_click' && onClick) {
        onClick(event.data.event);
      }
    }
  };
  useEffect(() => {
    window.addEventListener('message', handleParentMessage);

    // Cleanup: Remove the event listener when the component unmounts
    return () => {
      window.removeEventListener('message', handleParentMessage);
    };
  }, []);
  useEffect(() => {
    const convertImage = async () => {
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = img.width;
        canvas.height = img.height;
        ctx?.drawImage(img, 0, 0);
        const dataURL = canvas.toDataURL('image/png');
        setBase64Image(dataURL);
      };
      img.onerror = e => {
        throw new Error(`Error converting image: ${image} ${e}`);
      };
      img.src = image;
    };
    convertImage();
  }, [image]);
  useEffect(() => {
    const toastDiv = document.getElementById(toastId);
    if (typeof image === 'string') {
      if (base64Image) {
        sendToTopLevelParent(title, message, host, toastDiv?.innerHTML, toastId, containerId);
      }
    } else {
      sendToTopLevelParent(title, message, host, toastDiv?.innerHTML, toastId, containerId);
    }
  }, [base64Image]);
  return /*#__PURE__*/_jsx("div", {
    id: toastId,
    style: {
      display: 'none'
    },
    children: resolveImage(typeof image === 'string' ? base64Image : image)
  });
};